<template>
  <div>
    <b-container fluid>
      <SearchToolbar />
      <b-row class="align-items-center">
        <b-col lg="4">
          <h1>{{ combinedResults.length }} Search Results</h1>
        </b-col>
        <b-col>
          <b-alert variant="danger" show v-if="combinedResults.length >= 250" class="float-right">
            We found too many orders that match your search.  This is only showing the newest 250 orders.  Please narrow your search if you can't find the order you're looking for.
          </b-alert>
        </b-col>
      </b-row>
      <b-table
        striped hover
        :items="combinedResults" :fields="columns"
        v-if="loaded && combinedResults.length > 0"
        :sort-by="'datecreated'" :sort-desc="true"
        :sort-compare="sortCompare"
      >
        <template slot="soNum" slot-scope="data">
          <router-link :to="'/sales-order/view/' + data.item.soNum">
            {{ data.item.soNum }}
          </router-link>
          <WorkareaStatus :order="data.item.waOrder.order" :tryReload="true" @update-workarea-order="updateWorkareaOrder" />
          <FishbowlStatus :order="data.item.fbOrder" :tryReload="true" @update-fishbowl-order="updateFishbowlOrder" />
        </template>

        <template slot="billtoname" slot-scope="data">
          {{ merge(data.item, 'billtoname') }}<br>
          {{ data.item.email }}
        </template>

        <template slot="billtostreet" slot-scope="data">
          {{ merge(data.item, 'billtostreet') }}
        </template>

        <template slot="billtostate" slot-scope="data">
          {{ merge(data.item, 'billtostate') }}
        </template>

        <template slot="billtozip" slot-scope="data">
          {{ merge(data.item, 'billtozip') }}
        </template>

        <template slot="totalprice" slot-scope="data">
          {{ data.item.totalprice | formatDollars }}
        </template>

        <template slot="datecreated" slot-scope="data">
          <NiceDate :value="data.item.datecreated" v-if="data.item.datecreated != '...'"/>
          <span v-else>{{data.item.datecreated}}</span>
        </template>

        <template slot="dateissued" slot-scope="data">
          <NiceDate :value="data.item.dateissued" v-if="data.item.fbOrder.exists" />
          <span v-else>{{data.item.dateissued}}</span>
        </template>
        <template slot="orderPrivateNotes" slot-scope="data">
          <b-button @click="showModal(data.item)">
            Workarea Details
          </b-button>
        </template>

      </b-table>
      <div v-else-if="combinedResults.length == 0">
        No orders match your search query<br>
        <b-btn variant="primary" @click="searchWorkAreaRedirect()">
          Search Workarea
        </b-btn>
      </div>
      <div v-else>
        Searching for orders in Fishbowl. Please wait.
      </div>
    </b-container>

  <b-modal id="statusModal" ref="statusModalRef" centered hide-header hide-footer>
      <div class="d-block text-center" v-html="this.loadingMessage" />
      <center>
        <fingerprint-spinner :animation-duration="1500" :size="300" color="#293a93" class="my-3" />
      </center>
      <div slot="modal-footer" class="w-100" />
  </b-modal>
  <b-modal ref="orderPrivateNotesModal" hide-footer title="Workarea Details">
      <div class="d-block">
        <b-row class="align-items-center">
          <b-col cols="5">
            <span class="big text-monospace">
              {{ modalData.fishbowl.soNum }}
            </span>
          </b-col>
          <b-col v-if="this.workareaSalesOrder.exists">
            <b-button
              v-if="this.workareaSalesOrder.user_id"
              variant="outline-info"
              size="sm"
              target="_blank"
              :href="'https://www.andymark.com/admin/orders/'
              + this.workareaSalesOrder.id.toUpperCase()"
            >
              Open Website Order
            </b-button>
          </b-col>
          <b-col v-if="this.workareaSalesOrder.exists">
            <b-button
              v-if="this.modalData.fishbowl.soNum"
              variant="outline-primary"
              size="sm"
              target="_blank"
              :href="'https://www.andymark.com/admin/users/'
              + this.workareaSalesOrder.user_id"
            >
              Open Website Customer
            </b-button>
          </b-col>
        </b-row>

        <hr>
        <p v-if="workareaSalesOrder && workareaSalesOrder.orderNotes">
          <h5>Customer Order Comments</h5>
          <span v-html="workareaSalesOrder.orderNotes" />
        </p>
        <hr>

        <h5>Customer Account Private Notes</h5>
        <div v-if="workareaCustomerDetails && workareaCustomerDetails.data">
          <div v-if="workareaCustomerDetails.data.comments &&
          workareaCustomerDetails.data.comments.length">
          <p v-for="comment in workareaCustomerDetails.data.comments" :key="comment">
            {{ comment.updated_at | moment("MMMM D, YYYY") }} :
            <span v-html="comment.body"></span>
          </p>
          </div>
        </div>
        <hr>
        <h5>Order Private Notes:</h5>
        <p v-if="workareaSalesOrder && workareaSalesOrder.orderPrivateNotes && workareaSalesOrder.orderPrivateNotes.length">
          <p v-for="comment in workareaSalesOrder.orderPrivateNotes" :key="comment">
            <strong>{{ comment.updated_at | moment("MMMM D, YYYY") }} :</strong>
            <span v-html="comment.body"></span>
          </p>
        </p>
        <hr />
      </div>
      <b-btn class="mt-3" variant="outline-danger" block @click="hideModal">Close</b-btn>
  </b-modal>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dateFormat from 'dateformat';
import SearchToolbar from './SearchToolbar';
import NiceDate from '../../utility/NiceDate';
import FishbowlStatus from '../../utility/FishbowlStatus';
import WorkareaStatus from '../../utility/WorkareaStatus';
import { FingerprintSpinner } from 'epic-spinners';
import moment from 'moment';

export default {
  data() {
    return {
      loaded: false,
      overrides: {},
      loadingMessage: 'Loading Orders, please wait,',
      sortBy: 'datecreated',
      isDescending: true,
      columns: [
        { key: 'soNum', label: 'Order ID', sortable: true, },
        { key: 'datecreated', label: 'Date Created', sortable: true, },
        { key: 'dateissued', label: 'Date Issued', sortable: true, },
        { key: 'billtoname', label: 'Customer', sortable: true, },
        { key: 'billtostreet', label: 'Street', sortable: true, },
        { key: 'billtostate', label: 'State', sortable: true, },
        { key: 'billtozip', label: 'Zip', sortable: true, },
        { key: 'customerPO', label: 'Purchase Order #', sortable: true, },
        { key: 'totalprice', label: 'Total Price', sortable: true, },
        { key: 'orderPrivateNotes', label: 'Workarea Details', },
      ],
      mergeColumns: {
        billtoname:  'shiptoname',
        billtostreet: 'shiptostreet',
        billtostate: 'shiptostate',
        billtozip: 'shiptozip',
      },
      modalData: {
        fishbowl: {},
        workareaOrder: {},
        workareaCustomer: {},
      },
      orderSearch: {
        orderNumber: this.$route.query.orderNumber,
        email: this.$route.query.email,
        totalPrice: this.$route.query.totalPrice,
        customerPurchaseOrder: this.$route.query.customerPurchaseOrder,
        teamNumber: this.$route.query.teamNumber,
        ccLastFour: this.$route.query.ccLastFour,
        ipAddress: this.$route.query.ipAddress,
        address: {
          name: this.$route.query.name,
          street: this.$route.query.street,
          city: this.$route.query.city,
          zip: this.$route.query.zip,
          state: this.$route.query.state,
          country: this.$route.query.country,
        },
      },
    };
  },
  components: {
    SearchToolbar,
    NiceDate,
    FingerprintSpinner,
    WorkareaStatus,
    FishbowlStatus,
  },
  computed: {
    ...mapGetters({
      fishbowlSearchResults: 'fishbowlSearchResults',
      workareaSearchResults: 'workareaSearchResults',
      workareaSalesOrder: 'workareaSalesOrder',
      workareaCustomerDetails: 'workareaCustomerDetails',
    }),
    combinedResults() {
      if(this.loaded && this.fishbowlSearchResults.length > 0 || this.workareaSearchResults.length > 0) {
        try {
          let waIds = this.workareaSearchResults.filter( i => i ).map( o => o.order._id );
          let fbIds = this.fishbowlSearchResults.filter( i => i ).map( o => o.num );
          let union = Array.from(new Set([...waIds,...fbIds]));

          return union.map( (id) => {
            let fbOrder = this.fishbowlSearchResults[fbIds.indexOf(id)] || {soNum: id};
            if( this.fishbowlSearchResults[fbIds.indexOf(id)] ) { fbOrder.exists = true; } else { fbOrder.exists = false; }
            if(this.overrides[id] && this.overrides[id].fbOrder) { fbOrder = this.overrides[id].fbOrder; }

            let waOrder = this.workareaSearchResults[waIds.indexOf(id)] || { order: {soNum: id} };
            if( this.workareaSearchResults[waIds.indexOf(id)] ) { waOrder.order.exists = true; } else { waOrder.order.exists = false; }
            if(this.overrides[id] && this.overrides[id].waOrder) { waOrder = this.overrides[id].waOrder; }

            const waAddress = waOrder.shippings && waOrder.shippings[0]? waOrder.shippings[0].address : null;
            const customer_name = waAddress ? waAddress.first_name + waAddress.last_name : fbOrder.shiptoname;
            const customer_email = (waOrder.order ? waOrder.order.email : null) || fbOrder.email
            let total_price = fbOrder.exists ?
              (fbOrder.totalPrice / 100 || fbOrder.totalprice) :
              waOrder.exists ? waOrder.order.total_price.cents / 100 : 0
            return {
              fbOrder: fbOrder,
              waOrder: waOrder,
              exists: true,
              soNum: (fbOrder.num || waOrder.order._id),
              totalprice: total_price,
              email: customer_email,
              shiptoname: customer_name,
              shiptostreet: waAddress ? waAddress.street : fbOrder.shiptoaddress,
              shiptostate: waAddress ? waAddress.region : fbOrder.shiptostate,
              shiptozip: waAddress ? waAddress.postal_code : fbOrder.shiptozip,
              customerPO: fbOrder.customerPO || (waOrder.order ? waOrder.order.po_number : null),
              dateissued: fbOrder.dateIssued || fbOrder.dateissued,
              datecreated: waOrder.order.exists ? waOrder.order.created_at : fbOrder.datecreated,
            }
          });
        } catch(err) {
          console.log(err);
          return [];
        }
      } else {
        return [];
      }
    }
  },
  mounted() {
    this.showSpinner();
    this.clearWorkareaSearchResults();
    this.clearFishbowlSearchResults();
    this.deepTrim(this.orderSearch);
    Promise.all([
      this.searchFishbowlForOrders(this.orderSearch),
      this.searchWorkareaForOrders(this.orderSearch)
    ]).then( () => {
      console.log('done loading');
      this.loaded = true;
      this.hideSpinner();
    });
  },
  methods: {
    ...mapActions ({
      searchFishbowlForOrders: 'searchFishbowlForOrders',
      searchWorkareaForOrders: 'searchWorkareaForOrders',
      clearWorkareaSearchResults: 'clearWorkareaSearchResults',
      clearFishbowlSearchResults: 'clearFishbowlSearchResults',
      clearWorkareaOrder: 'clearWorkareaOrder',
      getWorkareaOrder: 'getWorkareaOrder',
    }),
    searchWorkAreaRedirect() {
      const params = this.getParams(this.$route.query);
      const url = `https://www.andymark.com/admin/orders?utf8=%E2%9C%93&order_status%5B%5D=placed&order_status%5B%5D=quote&order_status%5B%5D=checkout&sort=relevance&q=${params}&total_price_greater_than=&total_price_less_than=&commit=search_orders`;
      window.open(url, '_blank');
    },
    getParams(obj) {
      let params = '';
      Object.keys(obj).map((objectKey, index) => {
        const value = typeof (obj[objectKey]) === 'object' ? this.getParams(obj[objectKey]) : String(obj[objectKey]);
        if ((value !== 'undefined') && (value !== '') && params === '') params = value;
        else if (value !== 'undefined' && (value !== '')) params = `${params}+${value}`;
      });
      return params;
    },
    merge(item, key) {
      if (this.mergeColumns[key]) return item[key] || item[this.mergeColumns[key]]
      else return item[key]
    },
    sortCompare(a, b, key) {
      if (key == 'datecreated' || key == 'dateissued') {
        let aDate = moment(parseFloat(a.datecreated) > 10000000 ? parseFloat(a.datecreated) * 1000 : a.datecreated),
            bDate = moment(parseFloat(b.datecreated) > 10000000 ? parseFloat(b.datecreated) * 1000 : b.datecreated);
        return bDate > aDate ? -1 : aDate > bDate ? 1 : 0;
      } else if (key == 'totalprice') {
        return this.compareNumber(a, b, key);
      } else {
        return this.compareString(this.merge(a, key), this.merge(b, key));
      }
    },
    compareNumber(a, b, key) {
      let aNum = Number(a[key]), bNum = Number(b[key]);
      return aNum < bNum ? -1 : aNum > bNum ? 1 : 0;
    },
    compareString(a, b) {
      return this.toString(a).localeCompare(this.toString(b), undefined, { numeric: true });
    },
    toString(value) {
      if (!value) {
        return '';
      } else if (value instanceof Object) {
        return keys(value).sort().map(key => toString(value[key])).join(' ')
      } else { return String(value); }
    },
    showSpinner: function () {
      this.$refs.statusModalRef.show();
    },
    hideSpinner: function () {
      this.$refs.statusModalRef.hide();
    },
    updateWorkareaOrder(data) {
      if(!this.overrides[data.salesOrderNumber] || Object.keys(this.overrides[data.salesOrderNumber]).length == 0) { this.$set(this.overrides, data.salesOrderNumber, {}); }
      this.$set(this.overrides[data.salesOrderNumber], 'waOrder', data);
    },
    updateFishbowlOrder(data) {
      if(!this.overrides[data.salesOrderNumber] || Object.keys(this.overrides[data.salesOrderNumber]).length == 0) { this.$set(this.overrides, data.salesOrderNumber, {}); }
      this.$set(this.overrides[data.salesOrderNumber], 'fbOrder', data);
    },
    showModal(data) {
      this.clearWorkareaOrder().then( () => {
        this.modalData = {
          fishbowl: {},
          workareaOrder: {},
          workareaCustomer: {},
        };
        this.getWorkareaOrder(data.soNum).then( (wa) => {
          this.modalData.workareaOrder = this.workareaSalesOrder;
          this.modalData.fishbowl = data;
          console.log(this.modalData.fishbowl);
          this.$refs.orderPrivateNotesModal.show();
        });
      });
    },
    hideModal() {
      this.$refs.orderPrivateNotesModal.hide();
      this.modalData = {
        fishbowl: {},
        workareaOrder: {},
        workareaCustomer: {},
      };
    },
    deepTrim(obj) {
      for (const prop in obj) {
        let value = obj[prop];
        let type = typeof value;
        if (value != null && (type == 'string' || type == 'object') && obj.hasOwnProperty(prop)) {
          if (type == 'object') this.deepTrim(obj[prop]);
          else obj[prop] = obj[prop].trim();
        }
      }
    },
  },
  watch: {
    combinedResults: function () {
      if (this.loaded && this.fishbowlSearchResults && this.workareaSearchResults) {
        this.hideSpinner()
        if (this.fishbowlSearchResults.length == 1 && this.workareaSearchResults.length == 0) {
          this.$router.push({ path: '/sales-order/view/' + this.fishbowlSearchResults[0].num.trim() });
        } else if (this.fishbowlSearchResults.length == 0 && this.workareaSearchResults.length == 1) {
          this.$router.push({ path: '/sales-order/view/' + this.workareaSearchResults[0].order._id.trim() });
        } else if (this.fishbowlSearchResults.length == 1 && this.workareaSearchResults.length == 1 &&
                  (this.fishbowlSearchResults[0].num.localeCompare(this.workareaSearchResults[0].num))) {
          this.$router.push({ path: '/sales-order/view/' + this.fishbowlSearchResults[0].num.trim() });
        }
      }
    },
    overrides: function() {
      return this.combinedResults;
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/components/sales-order/search/SearchResults.vue